import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Surface, Text, useTheme } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';

import { getOnboardingState } from '@/src/api/apiService';
import { isAuthoritativeOnboardingState, routeForOnboardingState } from '@/src/utils/onboardingResume';
import { clearOwnerDriverStorage } from '@/src/features/owner/ownerDriverPrompt';
import { removeItem } from '@/src/lib/storage';

/**
 * Fail-closed resume screen. It deliberately does not inspect users/me,
 * employment, vehicles, licenses, or device storage: a retry may proceed only
 * after GET /api/onboarding/state returns a valid authoritative state.
 */
export default function OnboardingStateErrorScreen() {
  const { t } = useTranslation('onboarding');
  const theme = useTheme();
  const router = useRouter();
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(false);

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    setRetryError(false);
    try {
      const response = await getOnboardingState();
      if (response.ok && isAuthoritativeOnboardingState(response.data)) {
        router.replace(routeForOnboardingState(response.data));
        return;
      }
      setRetryError(true);
    } catch {
      setRetryError(true);
    } finally {
      setRetrying(false);
    }
  };

  const logout = async () => {
    await Promise.all([
      removeItem('userToken'),
      clearOwnerDriverStorage(),
    ]);
    router.replace('/');
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.center}>
        <Surface style={styles.card} elevation={1} testID="onboarding-state-error">
          <Text
            variant="headlineSmall"
            style={[styles.title, { color: theme.colors.error }]}
            accessibilityRole="header"
          >
            {t('resumeStateError.title', 'Não foi possível retomar a sua sessão')}
          </Text>
          <Text variant="bodyMedium" style={styles.body}>
            {t(
              'resumeStateError.body',
              'O estado seguro do onboarding não está disponível. Por segurança, não vamos adivinhar o próximo passo.',
            )}
          </Text>
          {retryError && (
            <Text
              accessibilityRole="alert"
              style={[styles.error, { color: theme.colors.error }]}
            >
              {t('resumeStateError.retryError', 'Ainda não foi possível confirmar o estado. Tente novamente.')}
            </Text>
          )}
          <Button
            mode="contained"
            onPress={() => void retry()}
            loading={retrying}
            disabled={retrying}
            testID="onboarding-state-error-retry"
          >
            {t('resumeStateError.retry', 'Tentar novamente')}
          </Button>
          <Button mode="text" onPress={() => void logout()} disabled={retrying}>
            {t('logout', 'Sair')}
          </Button>
        </Surface>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', padding: 16 },
  card: { alignSelf: 'center', borderRadius: 8, maxWidth: 520, padding: 24, width: '100%' },
  title: { marginBottom: 12, textAlign: 'center' },
  body: { marginBottom: 18, textAlign: 'center' },
  error: { marginBottom: 14, textAlign: 'center' },
});
